"use client";

import { useState } from "react";
import { deriveContext } from "@/lib/feedbackContext";
import { phContext, track } from "@/lib/analytics";
import { FeedbackForm } from "@/components/FeedbackForm";

const TYPE_LABEL: Record<string, string> = {
  template: "template",
  tool: "tool",
  guide: "guide",
  resource: "resource",
  page: "page",
};

/**
 * Inline end-of-page feedback block for content pages (templates/tools/guides/
 * resources). A quick "Was this helpful?" vote (PostHog only) plus a reveal-on-
 * click report form (FeedbackForm -> /api/feedback). Replaces the floating
 * feedback launcher: feedback now lives where the intent forms, attributed to
 * the exact content. Hidden in print.
 */
export function PageFeedback({ resourceType = "page" }: { resourceType?: string }) {
  const [vote, setVote] = useState<null | "yes" | "no">(null);
  const [showForm, setShowForm] = useState(false);
  const label = TYPE_LABEL[resourceType] || "page";

  function castVote(v: "yes" | "no") {
    if (vote) return;
    setVote(v);
    const ctx = deriveContext(resourceType);
    const ph = phContext();
    track("page_helpful", { helpful: v === "yes", resource_type: ctx.resourceType, slug: ctx.slug });
    // Notify the team inbox of the vote too (fire-and-forget; the worker emails
    // hello@ramsdocs.co.uk). The reveal-on-"no" form posts separately on submit.
    void fetch("/api/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        helpful: v === "yes",
        resource_type: ctx.resourceType,
        slug: ctx.slug,
        page_url: window.location.href,
        page_title: document.title,
        ph_distinct_id: ph.ph_distinct_id,
        ph_session_id: ph.ph_session_id,
      }),
    }).catch(() => {});
    if (v === "no") setShowForm(true);
  }

  return (
    <section className="no-print border-t border-line bg-surface">
      <div className="mx-auto max-w-3xl px-5 py-10">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <span className="font-display text-base font-semibold text-ink">Was this {label} helpful?</span>
          {vote === null ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => castVote("yes")}
                className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-[0.82rem] font-semibold text-body transition-colors hover:border-emerald hover:text-ink"
              >
                <svg viewBox="0 0 24 24" className="h-[15px] w-[15px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v11M3 13v6a2 2 0 0 0 2 2h11.5a2 2 0 0 0 2-1.7l1.2-7A2 2 0 0 0 18.7 10H14V5a2 2 0 0 0-2-2l-3 7" /></svg>
                Yes
              </button>
              <button
                type="button"
                onClick={() => castVote("no")}
                className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-[0.82rem] font-semibold text-body transition-colors hover:border-emerald hover:text-ink"
              >
                <svg viewBox="0 0 24 24" className="h-[15px] w-[15px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14V3M21 11V5a2 2 0 0 0-2-2H7.5a2 2 0 0 0-2 1.7l-1.2 7A2 2 0 0 0 6.3 14H11v5a2 2 0 0 0 2 2l3-7" /></svg>
                No
              </button>
            </div>
          ) : (
            <span className="text-sm text-body">Thanks for the feedback.</span>
          )}
        </div>

        <p className="mt-3 text-sm leading-relaxed text-body">
          Found something wrong, out of date, or missing?{" "}
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="font-semibold text-emerald hover:underline"
          >
            Report an issue or suggest a change
          </button>
        </p>

        {showForm && (
          <div className="mt-4">
            <FeedbackForm resourceType={resourceType} defaultCategory={vote === "no" ? "incorrect_info" : "suggestion"} />
          </div>
        )}
      </div>
    </section>
  );
}
